import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  List,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";

import { validateCitation, type CitationDraft, type CitationValidation } from "@/api/authoring";
import { AppLayout } from "@/components/AppLayout";
import { t } from "@/i18n";
import { safeHttpUrl } from "@/util";

/**
 * Citation editor / linter (phase2-admin-ui backlog #7).
 *
 * The citation corpus is **global** (single authority — a per-org corpus would
 * be two vocabularies free to drift) and is committed on the governed git path,
 * so the authoring BFF — org-layer-only by design (ADR-0008) — deliberately has
 * no citation *write*. This page is therefore the citation analogue of the rule
 * validator: fill the TracerCitation form, lint it (shape, id-collision against
 * the live corpus, DOI/PMID/URL recognition, ADR-0044 recency gaps), and hand
 * the validated YAML to whoever commits the base corpus. It never writes.
 */
const RECENCY = ["Current", "Needs Update", "Superseded"];

export function CitationEditorPage(): JSX.Element {
  const [form, setForm] = useState<CitationDraft>({
    id: "",
    type: "",
    title: "",
    year: null,
    publisher: null,
    url: null,
    recency_status: "Current",
    region_applicability: [],
    date_accessed: null,
  });

  const set = (patch: Partial<CitationDraft>): void => setForm((f) => ({ ...f, ...patch }));

  const validation = useMutation<CitationValidation, Error, CitationDraft>({
    mutationFn: (draft) => validateCitation(draft),
  });

  const canValidate = form.id.trim() !== "" && form.title.trim() !== "";

  return (
    <AppLayout breadcrumbs={[{ label: t("authoring.citation_editor_title") }]}>
      <Title order={1}>{t("authoring.citation_editor_title")}</Title>
      <Text c="dimmed" mt="xs" mb="md">
        {t("authoring.citation_editor_intro")}
      </Text>

      <Stack maw={640}>
        <TextInput
          label={t("authoring.citation_id")}
          placeholder="cit:aha-2025-001"
          value={form.id}
          onChange={(e) => set({ id: e.currentTarget.value })}
        />
        <TextInput
          label={t("authoring.citation_type")}
          placeholder="guideline"
          value={form.type}
          onChange={(e) => set({ type: e.currentTarget.value })}
        />
        <TextInput
          label={t("authoring.citation_title")}
          value={form.title}
          onChange={(e) => set({ title: e.currentTarget.value })}
        />
        <Group grow>
          <TextInput
            label={t("authoring.citation_year")}
            value={form.year != null ? String(form.year) : ""}
            onChange={(e) => {
              const raw = e.currentTarget.value.trim();
              const n = Number(raw);
              set({ year: raw !== "" && Number.isFinite(n) ? n : null });
            }}
          />
          <TextInput
            label={t("authoring.citation_publisher")}
            value={form.publisher ?? ""}
            onChange={(e) => set({ publisher: e.currentTarget.value || null })}
          />
        </Group>
        <TextInput
          label={t("authoring.citation_url")}
          placeholder="https://doi.org/10.1000/…"
          value={form.url ?? ""}
          onChange={(e) => set({ url: e.currentTarget.value || null })}
        />
        <Group grow>
          <Select
            label={t("authoring.citation_recency")}
            data={RECENCY}
            value={form.recency_status ?? "Current"}
            allowDeselect={false}
            onChange={(v) => v !== null && set({ recency_status: v })}
          />
          <TextInput
            label={t("authoring.citation_date_accessed")}
            placeholder="2025-01-31"
            value={form.date_accessed ?? ""}
            onChange={(e) => set({ date_accessed: e.currentTarget.value || null })}
          />
        </Group>
        <TextInput
          label={t("authoring.citation_regions")}
          placeholder="EU, US"
          value={(form.region_applicability ?? []).join(", ")}
          onChange={(e) =>
            set({
              region_applicability: e.currentTarget.value
                .split(",")
                .map((s) => s.trim())
                .filter((s) => s !== ""),
            })
          }
        />
        <Group>
          <Button
            disabled={!canValidate}
            loading={validation.isPending}
            onClick={() => validation.mutate(form)}
          >
            {t("authoring.citation_validate")}
          </Button>
        </Group>

        {validation.isError && (
          <Alert color="red" title={t("authoring.error_title")}>
            {validation.error.message}
          </Alert>
        )}
        {validation.data !== undefined && <ResultPanel result={validation.data} />}
      </Stack>
    </AppLayout>
  );
}

function ResultPanel({ result }: { result: CitationValidation }): JSX.Element {
  return (
    <Alert
      color={result.is_valid ? (result.id_exists ? "yellow" : "green") : "red"}
      title={result.is_valid ? t("authoring.citation_valid") : t("authoring.citation_invalid")}
    >
      {result.identifier_type !== null && (
        <Group gap="xs" mb="xs">
          <Text size="sm">{t("authoring.citation_identifier")}:</Text>
          <Badge variant="light">{result.identifier_type}</Badge>
        </Group>
      )}
      {result.id_exists && (
        <Text size="sm" c="orange" mb="xs">
          {t("authoring.citation_id_exists")}
        </Text>
      )}
      {result.errors.length > 0 && (
        <List size="sm">
          {result.errors.map((e) => (
            <List.Item key={e}>{e}</List.Item>
          ))}
        </List>
      )}
      {result.warnings.length > 0 && (
        <List size="sm" mt={result.errors.length > 0 ? "xs" : 0}>
          {result.warnings.map((w) => (
            <List.Item key={w}>⚠ {w}</List.Item>
          ))}
        </List>
      )}
      {result.normalized_citation !== null && (
        <Card withBorder mt="sm">
          <Text size="sm" fw={500}>
            {t("authoring.citation_preview")}
          </Text>
          <Text size="sm">
            {result.normalized_citation.title}
            {result.normalized_citation.year != null && ` (${result.normalized_citation.year})`}
          </Text>
          {typeof result.normalized_citation.url === "string" && (
            <Text size="xs" c="dimmed" style={{ wordBreak: "break-all" }}>
              {safeHttpUrl(result.normalized_citation.url)}
            </Text>
          )}
        </Card>
      )}
    </Alert>
  );
}
